import {ChangeDetectionStrategy, Component, computed, Injectable, input, Signal, signal} from "@angular/core";
import {TranslateModule} from "@ngx-translate/core";
import {MatSlideToggle, MatSlideToggleModule} from "@angular/material/slide-toggle";
import {MatButtonModule} from "@angular/material/button";
import {C3GrafKomponent} from "web-ng2/delt/komponenter/c3-graf/c3-graf";
import {ChartConfiguration, DataPoint} from "c3";
import {AlternativOgAntallModell, FemdeltVurdering, GruppeId, GruppeModell, GruppeRessursClient, MøtevurderingerModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {Temporal} from "temporal-polyfill";
import {toObservable, toSignal} from "@angular/core/rxjs-interop";
import {switchMap} from "rxjs";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {GruppeDatatjeneste} from "felles-ng2/tjenester/datalager/GruppeDatatjeneste";
import {første, siste} from "felles-ng2/støttefunksjoner/lister";
import {ZonedDateTimePeriodeLukket} from "felles-ng2/typer/ZonedDateTimePeriode";
import {RangeringAggregatOgTittel, RangeringResultaterSøylediagramKomponent, SøylediagramOppsett} from "../../../spørreundersøkelse/komponenter/rangering-resultater-søylediagram/rangering-resultater-søylediagram";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {alleEnuminnslag} from "felles-ng2/støttefunksjoner/enumer";
import {ZonedDateTimeSomPlainDatePipe} from "felles-ng2/pipes/ZonedDateTimeSomPlainDatePipe";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {TemporalFormatering} from "felles-ng2/tjenester/TemporalFormatering";
import {lesDateEllerZonedDateTimeSerialisert, lesZonedDateTimeSerialisert, skrivZonedDateTimeSerialisert} from "felles-ng2/støttefunksjoner/temporal";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {farge_profil_300_lillesand} from "felles-ng2/fargepalett";
import {RammeInnholdKomponent, RammeMedTittelKomponent} from "web-ng2/delt/komponenter/ramme-med-tittel/ramme-med-tittel";
import ZonedDateTime = Temporal.ZonedDateTime;

/** Viser graf over møteevalueringer for gruppe. */
@Component({
    selector: 'gruppe-moeteevalueringer',
    templateUrl: 'gruppe-møteevalueringer.html',
    styleUrl: 'gruppe-møteevalueringer.scss',
    host: {'data-testid': 'gruppe-møteevalueringer'},
    imports: [
        TranslateModule,
        MatSlideToggle,
        MatSlideToggleModule,
        MatButtonModule,
        C3GrafKomponent,
        TipsKomponent,
        ZonedDateTimeSomPlainDatePipe,
        FormaterPlainDatePipe,
        RangeringResultaterSøylediagramKomponent,
        ZonedDateTimeSomPlainDatePipe,
        FormaterPlainDatePipe,
        TipsKomponent,
        C3GrafKomponent,
        RammeMedTittelKomponent,
        RammeInnholdKomponent,

    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GruppeMøteevalueringerKomponent {

    gruppe = input.required<GruppeModell>();

    aktivert = computed(() => this.gruppe().oppsett.moetevurderingAktivert);
    moeteVurderinger: Signal<MøtevurderingerModell[]>;
    valgtMoete = signal<MøtevurderingerModell | null>(null);
    valgtMoeteAggregater = computed(() => erIkkeNil(this.valgtMoete()) ? this.moetevurderingSomResultater(this.valgtMoete()!) : null);
    c3Konfigurasjon = computed(() => this.beregnC3Konfigurasjon(this.moeteVurderinger()));

    constructor(private avhengigheter: GruppeMøteevalueringerKomponentAvhengigheter, private klokke: Klokke, private temporalFormatering: TemporalFormatering) {
        this.moeteVurderinger = toSignal(toObservable(this.gruppe).pipe(
          switchMap(gruppe => this.avhengigheter.finnMøteVurderinger(gruppe.id, this.klokke.nåSomZonedDateTime().subtract({years: 1}), this.klokke.nåSomZonedDateTime()))
        ), {initialValue: []});

        //  setTimeout(() => this.statsPunktValgt(3), 1000);
    }

    async endreAktiveringsstatusForMoeteevaluering(aktivert: boolean) {
        await this.avhengigheter.oppdaterMøtevurderingAktivert(this.gruppe().id, aktivert);
    }

    moetevurderingSomResultater(møte: MøtevurderingerModell): RangeringAggregatOgTittel[] {
        const fordeling = alleEnuminnslag<FemdeltVurdering>(FemdeltVurdering)
          .map(valg => {
              return {
                  antall: møte.vurderinger.filter(vurdering => vurdering === valg).length,
                  totaltAntallRespondenter: møte.vurderinger.length,
                  alternativ: {farge: farge_profil_300_lillesand, verdi: valg, tittel: this.femdeltVurderingSomCharCode(valg)}
              } as AlternativOgAntallModell
          })
        return [{id: 'a', tittel: '', tittelKort: '', aggregat: {vinnere: [], fordeling: fordeling}}];
    }

    diagramoppsett(): SøylediagramOppsett {
        return {}
    }

    //private hjelpemetoder

    //alternativt kunne ikoner fra material-font blitt brukt, se https://fonts.google.com/icons?icon.size=24&icon.color=%23e8eaed&icon.style=Sharp&icon.set=Material+Symbols&icon.query=sentiment&selected=Material+Symbols+Sharp:sentiment_content:FILL@0;wght@400;GRAD@0;opsz@24
    private femdeltVurderingSomCharCode(vurdering: FemdeltVurdering) {
        switch (vurdering) {
            case FemdeltVurdering.dårligst:
                return '\uea0a';
            case FemdeltVurdering.dårlig:
                return '\uea0c';
            case FemdeltVurdering.nøytral:
                return '\uea0e';
            case FemdeltVurdering.bra:
                return '\uea10';
            case FemdeltVurdering.best:
                return '\uea12';
        }
    }

    private beregnC3Konfigurasjon(vurderinger: MøtevurderingerModell[]): ChartConfiguration {
        const periode = ZonedDateTimePeriodeLukket.les({start: første(vurderinger)!.moetestart, slutt: siste(vurderinger)!.moetestart})
        var antallMåneder = periode.antallMåneder();

        const konfigurasjon = this.stdC3Konfig();
        konfigurasjon.data.json = vurderinger.map(møte => ({moetestart: lesZonedDateTimeSerialisert(møte.moetestart).epochMilliseconds, snittvurdering: møte.snittvurdering}))
        konfigurasjon.axis!.x!.tick!.count = Math.max(antallMåneder, 1);
        return konfigurasjon;
    }

    private stdC3Konfig(): ChartConfiguration {
        return {
            data: {
                json: [],
                keys: {
                    x: 'moetestart',
                    value: ['snittvurdering']
                },
                color: () => farge_profil_300_lillesand,
                type: "area-spline",
                onclick: (d, element) => this.statsPunktValgt(d.index)
            },
            //merk - funker ikke på touchdevice
            tooltip: {
                show: true,
                contents: (data: DataPoint[]) => {
                    const vurdering = this.moeteVurderinger()[data[0].index];
                    return `
                    <div class="tooltip-møte">
                        <i class="lederplan-ikon lederplan-ikon-emojikon${data[0].value}"></i>
                        ${vurdering.moetetittel} ${vurdering.moetenummer}, ${this.temporalFormatering.formaterZonedDateTime(lesZonedDateTimeSerialisert(vurdering.moetestart), {dateStyle: 'short'})}
                    </div>  
                    `
                }
            },
            axis: {
                x: {
                    type: 'timeseries',
                    tick: {
                        format: dato => this.temporalFormatering.formaterZonedDateTime(lesDateEllerZonedDateTimeSerialisert(dato as Date), {month: 'short'}),
                        outer: false,
                    }
                },
                y: {
                    min: FemdeltVurdering.dårligst,
                    max: FemdeltVurdering.best,
                    tick: {
                        values: [FemdeltVurdering.best, FemdeltVurdering.dårligst],
                        format: verdi => this.femdeltVurderingSomCharCode(verdi)
                    }
                }
            },
            legend: {show: false},
            point: {r: 4} //radius på datapunkt-markering i grafen
        };
    }

    //public for testbarhet
    public statsPunktValgt(indeks: number) {
        this.valgtMoete.set(this.moeteVurderinger()[indeks]);
    }
}

@Injectable()
export class GruppeMøteevalueringerKomponentAvhengigheter {

    constructor(private gruppeRessurs: GruppeRessursClient, private gruppeDatatjeneste: GruppeDatatjeneste) {
    }

    async oppdaterMøtevurderingAktivert(id: GruppeId, aktivert: boolean): Promise<void> {
        await this.gruppeDatatjeneste.oppdaterMøtevurderingAktivert(id, aktivert);
    }

    async finnMøteVurderinger(gruppeId: GruppeId, start: ZonedDateTime, slutt: ZonedDateTime): Promise<MøtevurderingerModell[]> {
        return await this.gruppeRessurs.finnMøteVurderinger(gruppeId, {start: skrivZonedDateTimeSerialisert(start), slutt: skrivZonedDateTimeSerialisert(slutt)}).somPromise();
    }
}
